/**
 * InTarget container — renders the ad at a specific DOM element identified by
 * a CSS selector provided in the placement config.
 *
 * insertWrapper will append inside the target if it is a block container element
 * (div, section, etc.) or insert after it otherwise — that decision lives in
 * insertWrapper, not here.
 *
 * renderLocation throws if the selector resolves to no element, surfacing the
 * failure to the handlePlacement error boundary which logs and exits cleanly.
 */
export class InTargetContainer {
  /**
   * @param {{ selector: string }} config  - placement container config from the ad response
   * @param {object} renderEnv             - captured render environment from getRenderEnv()
   */
  constructor(config, renderEnv) {
    this._selector  = config.selector;
    this._renderEnv = renderEnv;
    this._target    = null;
  }

  /**
   * Resolves the selector and returns true if a matching element exists in the document.
   * Caches the result — renderLocation reuses it without a second querySelector call.
   * @returns {boolean}
   */
  isSupported() {
    this._target = document.querySelector(this._selector);

    return this._target !== null;
  }

  /**
   * No additional positioning styles needed — target element provides the layout context.
   * @param {HTMLElement} _el
   */
  applyStyles(_el) {}

  /**
   * Returns the resolved target element for the CSS selector.
   *
   * Throws if isSupported() was not called first or the target was not found,
   * so that the handlePlacement error boundary can log and exit cleanly.
   *
   * @returns {Element}
   * @throws {Error} if the selector did not resolve to a DOM element
   */
  get renderLocation() {
    if (!this._target) {
      throw new Error(
        `InTarget selector '${this._selector}' did not resolve to a DOM element`
      );
    }

    return this._target;
  }
}
